/**
 * ship-sprites.ts — v4M4.a — per-(race, chassis) commissioned bitmap
 * sprite cache + loader.
 *
 * Mirrors the v4M1.8a backdrop pattern (`backdrop.ts`):
 *   1. A `Map<key, OffscreenCanvas>` cache keyed by `'<RaceId>:<chassis>'`.
 *   2. `loadShipSpriteImage(race, chassis, url)` fetches an
 *      `HTMLImageElement`, paints into a 128×128 OffscreenCanvas, and
 *      replaces the cache entry. Headless-safe + non-rejecting per the
 *      backdrop pattern — a missing file leaves the cache empty so the
 *      consumer's procedural fallback stays active.
 *   3. `getShipSprite(race, chassis)` is the cache read. Returns null
 *      when no sprite is loaded for the key.
 *
 * The render pipeline (`ship-draw.ts`) checks `getShipSprite(...)` for
 * each ship; if non-null it blits the bitmap (rotated by heading,
 * scaled by class) instead of running the procedural per-race
 * silhouette draw. Procedural draws remain in `ship-draw.ts` so the
 * game ships fully without commissioned sprites — Alpha 0.5 visual
 * read is preserved verbatim until sprite assets land in
 * `public/assets/ships/`.
 *
 * Sprite authoring contract (per `GRAPHICS.md §4.2` — to be amended
 * when v4M4.c lands commissioned ships):
 *   - 128×128 PNG, RGBA, transparent background.
 *   - Ship oriented NOSE-UP (-y direction in canvas coordinates).
 *   - Ship centred at (64, 64); fits within an inscribed 96 px circle
 *     so the v2M7.2.5 class-scale factor (0.75× — 2.0×) doesn't clip
 *     against the canvas edge at maximum class.
 *   - Per-race silhouette identity per GRAPHICS §4.3 — Terran steel
 *     blue / Kargath blood red / Reman stealth violet etc.; the
 *     render layer no longer recolours the sprite (no empire-accent
 *     tint applied to bitmap output — Empire.color drives selection
 *     ring + halo only).
 */

import type { RaceId } from '../state/types';

/**
 * Canonical sprite dimensions. The 128 px tile fits comfortably inside
 * the procedural `SHIP_DRAW_RADIUS = 7 px` × class-scale up to 2.0×
 * after a 0.11× render scale (so the bitmap renders at ~14 px screen
 * size at class 2, matching the procedural silhouette extent).
 */
const SHIP_SPRITE_SIZE = 128;

/**
 * Render scale applied at blit time so the 128 px source bitmap reads
 * at roughly the procedural silhouette extent (~14 px at class 2 / 1×
 * scale). Tweakable in v4M4.c playtest once art is in.
 */
export const SHIP_SPRITE_RENDER_SCALE = 0.11;

/** Cache key `<RaceId>:<chassis>` → painted offscreen canvas. */
const shipSpriteCache: Map<string, OffscreenCanvas> = new Map();

function cacheKey(race: RaceId, chassis: string): string {
  return `${race}:${chassis}`;
}

/**
 * Return the cached sprite for the given (race, chassis) pair, or null
 * when no sprite has been loaded for it. Consumers (`ship-draw.ts`)
 * branch on this — null falls through to the procedural silhouette.
 */
export function getShipSprite(
  race: RaceId,
  chassis: string,
): OffscreenCanvas | null {
  return shipSpriteCache.get(cacheKey(race, chassis)) ?? null;
}

/**
 * v4M4.a — load a commissioned ship sprite for the given (race,
 * chassis). Painted into a 128×128 OffscreenCanvas regardless of
 * source dimensions; commissioned sprites should be authored at
 * exactly 128×128.
 *
 * Promise always resolves (no rejection): a failed load leaves the
 * existing cache entry untouched, the procedural fallback stays
 * active, and other in-flight loads in the batch continue.
 * Headless-safe: when `OffscreenCanvas` or `Image` is undefined the
 * load is a no-op.
 */
export function loadShipSpriteImage(
  race: RaceId,
  chassis: string,
  url: string,
): Promise<void> {
  return new Promise((resolve) => {
    if (
      typeof OffscreenCanvas === 'undefined' ||
      typeof Image === 'undefined'
    ) {
      resolve();
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = new OffscreenCanvas(SHIP_SPRITE_SIZE, SHIP_SPRITE_SIZE);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve();
          return;
        }
        ctx.clearRect(0, 0, SHIP_SPRITE_SIZE, SHIP_SPRITE_SIZE);
        ctx.drawImage(img, 0, 0, SHIP_SPRITE_SIZE, SHIP_SPRITE_SIZE);
        shipSpriteCache.set(cacheKey(race, chassis), canvas);
      } catch {
        /* leave cache untouched — procedural fallback stays active */
      }
      resolve();
    };
    img.onerror = () => resolve();
    img.src = url;
  });
}

/**
 * Test-only — clear the sprite cache. Called from unit tests so that a
 * load in one test doesn't leak into the next.
 */
export function _resetShipSpriteCacheForTests(): void {
  shipSpriteCache.clear();
}
